import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import {
  AdditionalOption,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  GiftOrderCreateDto,
  GiftOrderDeliveryMode,
  GiftSelectedOption,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import { GiftCheckoutItemCard } from '../components/GiftCheckoutItemCard';
import GiftDeliveryModeSelector from '../components/GiftDeliveryModeSelector';
import { GiftPrivacyNotice } from '../components/GiftPrivacyNotice';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { GiftThanksPrintSummary } from '../containers/GiftThanksPrintSection/GiftThanksPrintSummary';
import { useGiftCheckoutPayment } from '../hooks/useGiftCheckoutPayment';
import { useGiftDeliveryAddressMemory } from '../hooks/useGiftDeliveryAddressMemory';
import { giftThanksPrintAtom } from '../state/giftThanksPrint.atom';
import { getGiftOrderDtoSingle } from '../utils/getGiftOrderDto';
import { getUnitSurcharge, resolveShippingFeeConfig } from '../utils/resolveDeliveryFee';
import { resolveAllowIndividualDelivery } from '../utils/resolveAllowIndividualDelivery';
import { scrollToInvalidField } from '../utils/scrollToInvalidField';
import {
  buildThanksPrintDto,
  resolveOrderThanksPrint,
} from '../utils/thanksPrintMessage';

interface CheckoutItem {
  itemId: string;
  itemName: string;
  itemImage: string;
  priceRetail: number;
  quantity: number;
  minOrderQuantity: number;
  orderUnit: number;
}

const GiftDirectCheckoutPage: FC = function GiftDirectCheckoutPage() {
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const bugoId = searchParams.get('bugoId');
  const initialQuantity = parseInt(searchParams.get('quantity') ?? '1', 10);

  // 상세페이지에서 전달된 선택옵션 (바로구매). 결제화면에서도 변경 가능하도록 state로 보관
  const [selectedOptions, setSelectedOptions] = useState<GiftSelectedOption[]>(
    () =>
      (location.state as { selectedOptions?: GiftSelectedOption[] } | null)
        ?.selectedOptions ?? [],
  );
  const optionDelta = useMemo(
    () => selectedOptions.reduce((s, o) => s + (o.priceDelta || 0), 0),
    [selectedOptions],
  );

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);

  // 상품 조회
  useGiftItemForStoreHook(itemId ?? '');
  const itemStatus = useRecoilValue(giftItemForStoreStatusAtomFamily(itemId ?? ''));
  const item = useRecoilValue(giftItemForStoreDataAtomFamily(itemId ?? ''));

  // bugo 데이터 조회
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  useEffect(() => {
    setTitle('주문/결제');
  }, [setTitle]);

  // 상품 정보 파생
  const checkoutItem: CheckoutItem | null = useMemo(() => {
    if (!item || !itemId) return null;

    const productName = item.name || item.giftItemInfo?.name || '';
    const mainImage = item.image || item.giftItemInfo?.image;
    const mainImageUrl =
      mainImage && typeof mainImage === 'object' && 'url' in mainImage
        ? (mainImage as Image).url
        : '';
    const priceRetail = item.priceRetail || 0;
    const minOrderQuantity = item.giftItemInfo?.minOrderQuantity || 1;
    const orderUnit = item.giftItemInfo?.orderUnit || 1;

    return {
      itemId,
      itemName: productName,
      itemImage: mainImageUrl,
      priceRetail,
      quantity: initialQuantity,
      minOrderQuantity,
      orderUnit,
    };
  }, [item, itemId, initialQuantity]);

  // 수량 상태
  const [quantity, setQuantity] = useState(initialQuantity);

  // 초기 수량 설정
  useEffect(() => {
    if (checkoutItem) {
      setQuantity(initialQuantity || checkoutItem.minOrderQuantity);
    }
  }, [checkoutItem, initialQuantity]);

  // 주문자 정보 (member 정보 기반)
  const [receiverName, setReceiverName] = useState('');
  const [receiverTel, setReceiverTel] = useState('');

  // 미입력 alert 시 스크롤 타겟
  const receiverNameRef = useRef<HTMLInputElement>(null);
  const receiverTelRef = useRef<HTMLInputElement>(null);
  const addressButtonRef = useRef<HTMLButtonElement>(null);

  // 배송 방식 관련 상태
  const [deliveryMode, setDeliveryMode] = useState<GiftOrderDeliveryMode>(
    GiftOrderDeliveryMode.Bulk,
  );
  const [bulkReceiverAddress, setBulkReceiverAddress] = useState('');
  const [bulkReceiverPostalCode, setBulkReceiverPostalCode] = useState('');
  const [bulkReceiverAddressDetail, setBulkReceiverAddressDetail] = useState('');

  // 배송형 상품 여부
  const isDeliveryItem = useMemo(() => {
    if (!item) return false;
    return item.giftItemInfo?.type !== GiftItemInfoType.Coupon;
  }, [item]);

  // 개별배송 허용 여부 (상품값 → 브랜드값 오버라이드)
  const allowIndividual = useMemo(() => {
    if (!isDeliveryItem) return true;
    return resolveAllowIndividualDelivery(item?.giftItemInfo);
  }, [isDeliveryItem, item]);

  // 개별배송 불가 상품이면 일괄배송(Bulk)으로 강제
  useEffect(() => {
    if (!allowIndividual && deliveryMode === GiftOrderDeliveryMode.Individual) {
      setDeliveryMode(GiftOrderDeliveryMode.Bulk);
    }
  }, [allowIndividual, deliveryMode]);

  // 최근 배송지 기억(localStorage) — 결제 실패/새로고침에도 배송지 보존 + 칩으로 재적용
  const {
    saved: savedAddress,
    applySaved: applySavedAddress,
    showChip: showSavedAddressChip,
    clear: clearSavedAddress,
    canClear: canClearAddress,
  } = useGiftDeliveryAddressMemory({
    address: bulkReceiverAddress,
    postalCode: bulkReceiverPostalCode,
    addressDetail: bulkReceiverAddressDetail,
    setAddress: setBulkReceiverAddress,
    setPostalCode: setBulkReceiverPostalCode,
    setAddressDetail: setBulkReceiverAddressDetail,
    enabled: isDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk,
  });

  // 감사메시지 인쇄 — 해당 브랜드 상품일 때만 (장바구니 결제와 동일한 공용 리졸버 사용). 주문당 1건.
  const thanksPrintInfo = useMemo(
    () =>
      resolveOrderThanksPrint(
        item ? [{ itemId: item._id, giftItemInfo: item.giftItemInfo }] : [],
      ),
    [item],
  );

  // 감사메시지 인쇄 입력값 (작성/수정은 GiftThanksPrintSummary가 atom에 보존)
  const thanksSelection = useRecoilValue(giftThanksPrintAtom);

  // member 정보로 초기값 설정
  useEffect(() => {
    if (bugoData?.member) {
      setReceiverName(bugoData.member.fullName || '');
      setReceiverTel(bugoData.member.phoneNumber || '');
    }
  }, [bugoData]);

  // GiftOrderCreateDto 생성 함수
  const createOrderDto = useCallback((): GiftOrderCreateDto | null => {
    if (!item || !bugoData || !checkoutItem) {
      return null;
    }

    const isBulk = isDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk;

    return getGiftOrderDtoSingle({
      giftItem: item,
      bugo: bugoData,
      quantity,
      selectedOptions: selectedOptions.length > 0 ? selectedOptions : undefined,
      receiverName,
      receiverPhoneNumber: receiverTel,
      deliveryMode: isDeliveryItem ? deliveryMode : undefined,
      bulkDeliveryAddress: isBulk
        ? {
            name: receiverName,
            phoneNumber: receiverTel,
            address: bulkReceiverAddress,
            addressDetail: bulkReceiverAddressDetail || undefined,
            postalCode: bulkReceiverPostalCode,
          }
        : undefined,
      // 감사메시지 인쇄 — 인쇄 대상 상품 + 작성된 내용이 있을 때만 (주문당 1건)
      thanksPrint: thanksPrintInfo
        ? buildThanksPrintDto(
            thanksSelection,
            thanksPrintInfo.config,
            thanksPrintInfo.brandId,
          )
        : undefined,
    });
  }, [
    item,
    bugoData,
    checkoutItem,
    quantity,
    selectedOptions,
    receiverName,
    receiverTel,
    isDeliveryItem,
    deliveryMode,
    bulkReceiverAddress,
    bulkReceiverAddressDetail,
    bulkReceiverPostalCode,
    thanksPrintInfo,
    thanksSelection,
  ]);

  // Bulk 배송 시 배송지 유효성 검증
  const validateDeliveryAddress = useCallback(() => {
    if (!isDeliveryItem || deliveryMode !== GiftOrderDeliveryMode.Bulk) return true;
    if (!bulkReceiverAddress.trim()) {
      alert('배송지 주소를 입력해주세요.');
      scrollToInvalidField(addressButtonRef.current);
      return false;
    }
    return true;
  }, [isDeliveryItem, deliveryMode, bulkReceiverAddress]);

  // 결제 훅 사용
  const { handlePayment, isProcessing } = useGiftCheckoutPayment({
    createOrderDto,
    bugoId,
    receiverName,
    receiverTel,
    hasItems: !!checkoutItem,
    validateDeliveryAddress,
    receiverNameRef,
    receiverTelRef,
  });

  // 수량 증가
  const increaseQuantity = useCallback(() => {
    if (!checkoutItem) return;
    setQuantity((prev) => prev + checkoutItem.orderUnit);
  }, [checkoutItem]);

  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    if (!checkoutItem) return;
    setQuantity((prev) => {
      const newQty = prev - checkoutItem.orderUnit;
      return newQty >= checkoutItem.minOrderQuantity ? newQty : prev;
    });
  }, [checkoutItem]);

  // 개당 소량주문 추가금 (수량이 면제 기준 이상이면 0). 상품값 → 브랜드값 오버라이드
  const unitSurcharge = useMemo(() => {
    if (!item || !isDeliveryItem) return 0;
    return getUnitSurcharge(item.giftItemInfo, quantity);
  }, [item, isDeliveryItem, quantity]);

  // 상품 금액 (옵션 + 소량주문 추가금까지 단가에 포함)
  const goodsTotal = useMemo(() => {
    if (!checkoutItem) return 0;
    return (checkoutItem.priceRetail + optionDelta + unitSurcharge) * quantity;
  }, [checkoutItem, quantity, optionDelta, unitSurcharge]);

  // 추가금 면제까지 더 담으면 되는 수량 — 구매 유도용
  const surchargeNudge = useMemo(() => {
    if (!item || !isDeliveryItem) return null;
    const { shippingFee, freeShippingMinQuantity } = resolveShippingFeeConfig(
      item.giftItemInfo,
    );
    if (
      !shippingFee ||
      shippingFee <= 0 ||
      freeShippingMinQuantity == null ||
      quantity >= freeShippingMinQuantity
    ) {
      return null;
    }
    return {
      remain: freeShippingMinQuantity - quantity,
      minQty: freeShippingMinQuantity,
      unitSurcharge: shippingFee,
    };
  }, [item, isDeliveryItem, quantity]);

  // 총 결제 금액 = 상품 금액 (추가금 포함)
  const totalPrice = goodsTotal;

  // 로딩 중
  if (itemStatus !== 'success' || !item || !checkoutItem) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // bugoId가 없으면 리다이렉트
  if (!bugoId) {
    navigate('/gift-store');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 1. 주문자 정보 */}
      <div className="bg-white p-4">
        <h3 className="mb-3 font-bold">주문자 정보</h3>
        <div className="space-y-3">
          <TextField
            ref={receiverNameRef}
            scrollOnFocus={true}
            className="w-full"
            placeholder="주문자 성함"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
          />
          <TextField
            ref={receiverTelRef}
            scrollOnFocus={true}
            type="tel"
            className="w-full"
            placeholder="휴대전화번호"
            value={phoneNumberStringFormator(receiverTel)}
            maxLength={13}
            onChange={(e) => setReceiverTel(e.target.value.replace(/[^0-9]/gi, ''))}
          />
        </div>
      </div>

      {/* 2. 구매 상품 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">구매 상품</h3>
        <div className="space-y-3">
          <GiftCheckoutItemCard
            itemName={checkoutItem.itemName}
            itemImage={checkoutItem.itemImage}
            priceRetail={checkoutItem.priceRetail}
            quantity={quantity}
            minOrderQuantity={checkoutItem.minOrderQuantity}
            orderUnit={checkoutItem.orderUnit}
            selectedOptions={selectedOptions}
            additionalOptions={
              item.giftItemInfo?.additionalOptions as
                | AdditionalOption<Image>[]
                | undefined
            }
            onChangeOptions={setSelectedOptions}
            onIncrease={increaseQuantity}
            onDecrease={decreaseQuantity}
            itemType={item.giftItemInfo?.type}
            deliveryMode={deliveryMode}
            unitSurcharge={unitSurcharge}
            surchargeMinQty={
              resolveShippingFeeConfig(item.giftItemInfo).freeShippingMinQuantity
            }
          />
        </div>

        {/* 대량구매 혜택 유도 */}
        {surchargeNudge && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-bold">{surchargeNudge.remain}개</span>만 더 담으면 개당{' '}
            <span className="font-bold">
              {formatPrice(surchargeNudge.unitSurcharge)}원
            </span>{' '}
            더 저렴해져요! (
            <span className="font-bold">{surchargeNudge.minQty}개 이상</span>)
          </div>
        )}
      </div>

      {/* 2-1. 감사 메시지 인쇄 (감사 인쇄 가능 상품일 때) */}
      {thanksPrintInfo && (
        <div className="mt-2">
          <GiftThanksPrintSummary
            thanksPrint={thanksPrintInfo.config}
            brandId={thanksPrintInfo.brandId}
            bugoId={bugoId}
            eligibleCount={thanksPrintInfo.eligibleCount}
          />
        </div>
      )}

      {/* 3. 배송 방식 선택 (배송형 상품일 때만) */}
      {isDeliveryItem && (
        <div className="mt-2">
          <GiftDeliveryModeSelector
            deliveryMode={deliveryMode}
            onDeliveryModeChange={setDeliveryMode}
            receiverAddress={bulkReceiverAddress}
            onReceiverAddressChange={setBulkReceiverAddress}
            receiverPostalCode={bulkReceiverPostalCode}
            onReceiverPostalCodeChange={setBulkReceiverPostalCode}
            receiverAddressDetail={bulkReceiverAddressDetail}
            onReceiverAddressDetailChange={setBulkReceiverAddressDetail}
            allowIndividual={allowIndividual}
            addressButtonRef={addressButtonRef}
            savedAddress={savedAddress}
            showSavedChip={showSavedAddressChip}
            onApplySaved={applySavedAddress}
            showClear={canClearAddress}
            onClear={clearSavedAddress}
          />
        </div>
      )}

      {/* 4. 최종 결제 금액 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">결제 금액</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">상품 금액</span>
            <span>{formatPrice(goodsTotal)}원</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="font-bold">총 결제 금액</span>
            <span className="text-lg font-bold text-red-500">
              {formatPrice(totalPrice)}원
            </span>
          </div>
        </div>
      </div>

      {/* 5. 개인정보 동의 (체크 없이 표시만) */}
      <div className="mt-2 bg-white p-4">
        <GiftPrivacyNotice />
      </div>

      {/* 6. 결제 도움 안내 */}
      <div className="mt-2 bg-white">
        <GiftCallHelperBox />
      </div>

      {/* 7. 하단 고정 결제 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-3 shadow-lg">
        <div className="mx-auto max-w-lg">
          <button
            type="button"
            onClick={handlePayment}
            disabled={isProcessing}
            className="w-full rounded-lg bg-red-500 py-3 text-lg font-bold text-white disabled:bg-gray-300"
          >
            {isProcessing ? '결제 진행 중...' : `${formatPrice(totalPrice)}원 결제하기`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftDirectCheckoutPage;
